import { useCallback, useEffect, useRef } from "react";

import { logError } from "utility/Function";
import { platformGet, PlatformRestError } from "./platform";

// Athena answers a first view in about ten seconds; a page not ready a minute
// in, whether still running or with a request that never answered, is
// treated as failed rather than left spinning.
export const POLL_MS = 2_500;
export const GIVE_UP_AFTER_MS = 60_000;

// An expired session, or web running ahead of Platform.
const UNREMARKABLE_STATUSES = [401, 404];

export type PollResponse =
  | { status: "ready" }
  | { status: "running"; handle: string };

export type PollRequest<Response extends PollResponse> = {
  // Read at every start, a restart included, so it can name what an earlier
  // answer of the same request settled.
  startPath: () => string;
  // Every answer the request still wants, before it is checked again or
  // finished; throwing fails the request.
  onAnswer: (response: Response) => void;
  onError: () => void;
};

/**
 * Asks a dashboard endpoint for figures that may still be calculating, one
 * request at a time.
 *
 * A 202 is checked again with its newest handle until the figures are ready.
 * A handle that can no longer be answered (400: expired, or its queries gone)
 * is started afresh once; a second refusal is a real failure. A request not
 * finished within the give-up is failed. Starting a request, `cancel`, or
 * unmounting drops whatever the previous one would still answer.
 */
export function useDashboardPolling({
  path,
  name,
  pollMs,
  giveUpAfterMs,
}: {
  // The endpoint, which takes `?handle=` to check a running request.
  path: string;
  // Names the page in what is logged.
  name: string;
  pollMs: number;
  giveUpAfterMs: number;
}) {
  const run = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const deadline = useRef<ReturnType<typeof setTimeout>>();
  const timing = useRef({ pollMs, giveUpAfterMs });
  timing.current = { pollMs, giveUpAfterMs };

  const stop = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    if (deadline.current) clearTimeout(deadline.current);
  }, []);

  const cancel = useCallback(() => {
    run.current += 1;
    stop();
  }, [stop]);

  const request = useCallback(
    <Response extends PollResponse>({
      startPath,
      onAnswer,
      onError,
    }: PollRequest<Response>) => {
      cancel();
      const current = run.current;
      let restarted = false;

      const fail = (error: unknown) => {
        cancel();
        const code =
          error instanceof PlatformRestError ? error.status : undefined;
        if (code === undefined || !UNREMARKABLE_STATUSES.includes(code)) {
          logError(`Company dashboard ${name} failed to load`, error);
        }
        onError();
      };

      const answer = (response: Response) => {
        if (current !== run.current) return;
        onAnswer(response);
        if (current !== run.current) return;
        const polled: PollResponse = response;
        if (polled.status === "ready") {
          stop();
          return;
        }
        timer.current = setTimeout(
          () => check(polled.handle),
          timing.current.pollMs
        );
      };

      const start = () =>
        platformGet<Response>(startPath())
          .then(answer)
          .catch((error) => {
            if (current === run.current) fail(error);
          });

      const check = (handle: string) =>
        platformGet<Response>(`${path}?handle=${encodeURIComponent(handle)}`)
          .then(answer)
          .catch((error) => {
            if (current !== run.current) return;
            if (
              !restarted &&
              error instanceof PlatformRestError &&
              error.status === 400
            ) {
              restarted = true;
              start();
              return;
            }
            fail(error);
          });

      deadline.current = setTimeout(() => {
        if (current === run.current) {
          fail(new Error(`Company dashboard ${name} timed out`));
        }
      }, timing.current.giveUpAfterMs);
      start();
    },
    [path, name, cancel, stop]
  );

  // A remount (Fast Refresh, StrictMode) drops the answer in flight.
  useEffect(() => cancel, [cancel]);

  return { request, cancel };
}
